import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config';
import {
  GraduationCap, Users, BookOpen, User, Search, Plus, Edit, Trash2,
  X, CheckCircle, AlertCircle, ChevronLeft, ChevronRight, RefreshCw,
  Filter, Save
} from 'lucide-react';
import ActionIconButton from '../../Components/ActionIconButton';
import ActionsCell from '../../Components/ActionsCell';

const GRADE_LIST = ['BV1','BV2','BV3','1','2','3','4','5','6','7','8','9','10','11','12'];
const STAGE_LIST = ['Foundational Stage','Preparatory Stage','Middle Stage','Secondary Stage'];
const PAGE_SIZE = 20;

const apiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('accessToken');
  const res = await fetch(`${API_BASE_URL}${endpoint}`, {
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  if (res.status === 204) return null;
  return res.json();
};

const stageColor = (stage) => {
  const map = {
    'Foundational Stage': 'bg-blue-100 text-blue-800',
    'Preparatory Stage': 'bg-green-100 text-green-800',
    'Middle Stage': 'bg-purple-100 text-purple-800',
    'Secondary Stage': 'bg-orange-100 text-orange-800',
  };
  return map[stage] || 'bg-gray-100 text-gray-700';
};

const ClassManagement = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Data
  const [stats, setStats] = useState(null);
  const [classes, setClasses] = useState([]);
  const [total, setTotal] = useState(0);
  const [teachers, setTeachers] = useState([]);

  // Filters & pagination
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ search: '', stage: '', grade: '', section: '', teacher_id: '' });

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingClass, setEditingClass] = useState(null); // null = create, obj = edit
  const [form, setForm] = useState({ name: '', description: '', grade: '', section: '', teacher_id: '' });
  const [saving, setSaving] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);


  const notify = (type, msg) => {
    if (type === 'success') { setSuccess(msg); setTimeout(() => setSuccess(null), 3000); }
    else { setError(msg); setTimeout(() => setError(null), 5000); }
  };

  const loadStats = useCallback(async () => {
    try {
      const data = await apiCall('/admin/classes/stats');
      setStats(data);
    } catch (e) { notify('error', `Failed to load stats: ${e.message}`); }
  }, []);

  const loadTeachers = useCallback(async () => {
    try {
      const data = await apiCall('/admin/classes/teachers');
      setTeachers(data || []);
    } catch (e) { /* non-critical */ }
  }, []);

  const loadClasses = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: PAGE_SIZE });
      if (filters.search) params.append('search', filters.search);
      if (filters.stage) params.append('stage', filters.stage);
      if (filters.grade) params.append('grade', filters.grade);
      if (filters.section) params.append('section', filters.section);
      if (filters.teacher_id) params.append('teacher_id', filters.teacher_id);
      const data = await apiCall(`/admin/classes?${params}`);
      setClasses(data.classes || []);
      setTotal(data.total || 0);
    } catch (e) { notify('error', `Failed to load classes: ${e.message}`); }
    finally { setLoading(false); }
  }, [page, filters]);

  useEffect(() => { loadStats(); loadTeachers(); }, [loadStats, loadTeachers]);
  useEffect(() => { loadClasses(); }, [loadClasses]);

  const openCreate = () => {
    setEditingClass(null);
    setForm({ name: '', description: '', grade: '', section: '', teacher_id: '' });
    setShowModal(true);
  };

  const openEdit = (cls) => {
    setEditingClass(cls);
    setForm({
      name: cls.name || '',
      description: cls.description || '',
      grade: cls.grade || '',
      section: cls.section || '',
      teacher_id: cls.teacher_id || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { notify('error', 'Class name is required'); return; }
    setSaving(true);
    try {
      if (editingClass) {
        await apiCall(`/admin/classes/${editingClass.id}`, {
          method: 'PUT', body: JSON.stringify(form),
        });
        notify('success', 'Class updated successfully');
      } else {
        await apiCall('/admin/classes', { method: 'POST', body: JSON.stringify(form) });
        notify('success', 'Class created successfully');
      }
      setShowModal(false);
      loadClasses();
      loadStats();
    } catch (e) { notify('error', e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiCall(`/admin/classes/${deleteTarget.id}`, { method: 'DELETE' });
      notify('success', `"${deleteTarget.name}" deleted`);
      setDeleteTarget(null);
      loadClasses();
      loadStats();
    } catch (e) { notify('error', e.message); }
    finally { setDeleting(false); }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);


  return (
    <>
      <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">

          {/* Notifications */}
          {error && (
            <div className="fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 bg-red-500 text-white" style={{ animation: 'slideInRight 0.3s ease-out' }}>
              <AlertCircle className="w-5 h-5" /><span>{error}</span>
              <button onClick={() => setError(null)}><X className="w-4 h-4" /></button>
            </div>
          )}
          {success && (
            <div className="fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 bg-green-500 text-white" style={{ animation: 'slideInRight 0.3s ease-out' }}>
              <CheckCircle className="w-5 h-5" /><span>{success}</span>
              <button onClick={() => setSuccess(null)}><X className="w-4 h-4" /></button>
            </div>
          )}

          {/* Header */}
          <div className="mb-8">
            <div className="bg-gradient-to-r from-orange-50 to-blue-50 rounded-2xl p-8 border border-orange-100">
              <div className="flex items-start gap-6">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#D67C2C] to-orange-600 flex items-center justify-center shadow-lg flex-shrink-0">
                  <GraduationCap className="w-10 h-10 text-white" />
                </div>
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">Class Management</h1>
                  <p className="text-gray-600 text-lg mb-4">View, create, edit and delete all classes across all teachers in your school</p>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2 text-gray-700"><div className="w-2 h-2 rounded-full bg-blue-500"></div><span>All stages & grades</span></div>
                    <div className="flex items-center gap-2 text-gray-700"><div className="w-2 h-2 rounded-full bg-green-500"></div><span>Assign to any teacher</span></div>
                    <div className="flex items-center gap-2 text-gray-700"><div className="w-2 h-2 rounded-full bg-purple-500"></div><span>Full CRUD control</span></div>
                  </div>
                </div>
                <button
                  onClick={openCreate}
                  className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-[#D67C2C] to-orange-600 text-white rounded-xl font-medium hover:shadow-lg transition-all duration-200 flex-shrink-0"
                >
                  <Plus className="w-5 h-5" /> New Class
                </button>
              </div>
            </div>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[
                { label: 'Total Classes', value: stats.total_classes, color: 'blue', icon: <GraduationCap className="w-6 h-6 text-blue-600" /> },
                { label: 'Total Students', value: stats.total_students, color: 'green', icon: <Users className="w-6 h-6 text-green-600" /> },
                { label: 'Total Assignments', value: stats.total_assignments, color: 'purple', icon: <BookOpen className="w-6 h-6 text-purple-600" /> },
                { label: 'Teachers with Classes', value: stats.teachers_with_classes, color: 'orange', icon: <User className="w-6 h-6 text-orange-600" /> },
              ].map(({ label, value, color, icon }) => (
                <div key={label} className={`bg-white rounded-xl shadow-md border border-gray-200 p-5`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">{label}</p>
                      <p className={`text-3xl font-bold text-${color}-600 mt-1`}>{value ?? 0}</p>
                    </div>
                    <div className={`p-3 rounded-full bg-${color}-100`}>{icon}</div>
                  </div>
                </div>
              ))}
            </div>
          )}


          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-5 h-5 text-[#D67C2C]" />
              <h3 className="font-semibold text-gray-800">Filters</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text" placeholder="Search class name..."
                  value={filters.search}
                  onChange={e => { setFilters(f => ({ ...f, search: e.target.value })); setPage(1); }}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                />
              </div>
              <select value={filters.stage} onChange={e => { setFilters(f => ({ ...f, stage: e.target.value, grade: '' })); setPage(1); }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent">
                <option value="">All Stages</option>
                {STAGE_LIST.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={filters.grade} onChange={e => { setFilters(f => ({ ...f, grade: e.target.value })); setPage(1); }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent">
                <option value="">All Grades</option>
                {GRADE_LIST.map(g => <option key={g} value={g}>Grade {g}</option>)}
              </select>
              <input type="text" placeholder="Section (e.g. A)"
                value={filters.section}
                onChange={e => { setFilters(f => ({ ...f, section: e.target.value })); setPage(1); }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
              />
              <select value={filters.teacher_id} onChange={e => { setFilters(f => ({ ...f, teacher_id: e.target.value })); setPage(1); }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent">
                <option value="">All Teachers</option>
                {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            {(filters.search || filters.stage || filters.grade || filters.section || filters.teacher_id) && (
              <button onClick={() => { setFilters({ search: '', stage: '', grade: '', section: '', teacher_id: '' }); setPage(1); }}
                className="mt-3 text-sm text-[#D67C2C] hover:underline font-medium">
                Clear all filters
              </button>
            )}
          </div>


          {/* Table */}
          <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
            <div className="p-5 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">All Classes</h2>
                <p className="text-sm text-gray-500 mt-0.5">{total} class{total !== 1 ? 'es' : ''} found</p>
              </div>
              <button onClick={() => { loadClasses(); loadStats(); }}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-sm">
                <RefreshCw className="w-4 h-4" /> Refresh
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <RefreshCw className="w-8 h-8 text-[#D67C2C] animate-spin" />
                <span className="ml-3 text-gray-600">Loading classes...</span>
              </div>
            ) : classes.length === 0 ? (
              <div className="text-center py-16">
                <GraduationCap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg font-medium">No classes found</p>
                <p className="text-gray-400 text-sm mt-1">Try adjusting your filters or create a new class</p>
                <button onClick={openCreate} className="mt-4 flex items-center gap-2 px-4 py-2 bg-[#D67C2C] text-white rounded-lg mx-auto hover:bg-orange-600 transition-colors">
                  <Plus className="w-4 h-4" /> Create Class
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {['Class Name','Grade','Section','Stage','Teacher','Students','Assignments','Actions'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {classes.map(cls => (
                      <tr key={cls.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-4">
                          <div className="font-medium text-gray-900 text-sm">{cls.name}</div>
                          {cls.description && <div className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">{cls.description}</div>}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">{cls.grade || '—'}</td>
                        <td className="px-4 py-4 text-sm text-gray-600">{cls.section || '—'}</td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${stageColor(cls.stage)}`}>
                            {cls.stage || 'Unknown'}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          {cls.teacher_name
                            ? <div><div className="text-sm font-medium text-gray-900">{cls.teacher_name}</div><div className="text-xs text-gray-400">{cls.teacher_email}</div></div>
                            : <span className="text-xs text-gray-400 italic">Unassigned</span>}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700 font-medium">{cls.student_count}</td>
                        <td className="px-4 py-4 text-sm text-gray-700 font-medium">{cls.assignment_count}</td>
                        <td className="px-4 py-4">
                          <ActionsCell>
                            <ActionIconButton icon={Edit} tone="edit" onClick={() => openEdit(cls)} title="Edit" />
                            <ActionIconButton icon={Trash2} tone="delete" onClick={() => setDeleteTarget(cls)} title="Delete" />
                          </ActionsCell>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page === 1}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let n;
                    if (totalPages <= 5) n = i + 1;
                    else if (page <= 3) n = i + 1;
                    else if (page >= totalPages - 2) n = totalPages - 4 + i;
                    else n = page - 2 + i;
                    return (
                      <button key={n} onClick={() => setPage(n)}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${page === n ? 'bg-[#D67C2C] text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
                        {n}
                      </button>
                    );
                  })}
                  <button onClick={() => setPage(p => Math.min(p + 1, totalPages))} disabled={page === totalPages}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>


          {/* Create / Edit Modal */}
          {showModal && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#D67C2C] flex items-center justify-center">
                      <GraduationCap className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      {editingClass ? 'Edit Class' : 'Create New Class'}
                    </h2>
                  </div>
                  <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Class Name <span className="text-red-500">*</span></label>
                    <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="e.g. Grade 5 - Section A"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                      placeholder="Optional description..."
                      rows={2}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent resize-none" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                      <select value={form.grade} onChange={e => setForm(f => ({ ...f, grade: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent">
                        <option value="">Select grade</option>
                        {GRADE_LIST.map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Section</label>
                      <input type="text" value={form.section} onChange={e => setForm(f => ({ ...f, section: e.target.value }))}
                        placeholder="e.g. A, B, C"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assign to Teacher</label>
                    <select value={form.teacher_id} onChange={e => setForm(f => ({ ...f, teacher_id: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent">
                      <option value="">Unassigned</option>
                      {teachers.map(t => <option key={t.id} value={t.id}>{t.name} — {t.email}</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100">
                  <button onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium transition-colors">
                    Cancel
                  </button>
                  <button onClick={handleSave} disabled={saving}
                    className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-[#D67C2C] to-orange-600 text-white rounded-lg font-medium hover:shadow-lg transition-all duration-200 disabled:opacity-60">
                    {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {saving ? 'Saving...' : (editingClass ? 'Save Changes' : 'Create Class')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirm Modal */}
          {deleteTarget && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <Trash2 className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Delete Class</h3>
                    <p className="text-sm text-gray-500">This action cannot be undone</p>
                  </div>
                </div>
                <p className="text-gray-700 mb-2">
                  Are you sure you want to delete <span className="font-semibold">"{deleteTarget.name}"</span>?
                </p>
                <p className="text-sm text-red-600 bg-red-50 rounded-lg p-3 mb-6">
                  This will permanently delete the class along with all its <strong>{deleteTarget.assignment_count} assignment(s)</strong> and their submissions.
                </p>
                <div className="flex items-center justify-end gap-3">
                  <button onClick={() => setDeleteTarget(null)} disabled={deleting}
                    className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium">
                    Cancel
                  </button>
                  <button onClick={handleDelete} disabled={deleting}
                    className="flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-60">
                    {deleting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    {deleting ? 'Deleting...' : 'Delete Class'}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default ClassManagement;
